// Offline-Datenzugriff: Spieltag-Seite (leiter-gekoppelt), Referenzdaten (rein lesend), Check-in.
// Spieltage werden AUSSCHLIESSLICH für Spieltagleiter (EventPlayer.isOrganizing === true) gecacht: der
// geöffnete, der nächste und jeder weitere geleitete der nächsten LEADER_PREFETCH_DAYS Tage. Referenzdaten
// (Wikis + Zusätze-Katalog, ohne Personenbezug) laufen über cachedGet und werden für alle auf der Startseite
// vorgeladen.
//
// Verbindungsmodell (siehe connectivity.js) — die App ist im Online- ODER im Offline-MODUS:
//  - Online: Netz zuerst. Liegt eine lokale Kopie als Fallback bereit, wird höchstens CACHE_TIMEOUT_MS
//    gewartet. Netzwerkfehler/Timeout schaltet (zentral im API-Interceptor) in den Offline-Modus und
//    die Kopie wird geliefert.
//  - Offline: sofort die lokale Kopie, kein Netzverkehr. Zurück nur per reconnect() oder Reload.

import api from '../components/wrapper/API';
import * as db from './db';
import { isOffline, leaveOffline } from './connectivity';
import { applyPresenceWithAutoPaid, pickPresencePayment } from '../util/payment';
// Circular on purpose (the hook reads cachedGet/REFERENCE_URLS); both sides only use it at call time.
import { preloadParticipantGroups } from '../hooks/useParticipantGroups';
import { preloadRoleSettings } from '../hooks/useRoleSettings';
import { preloadWikis } from '../hooks/useWikis';
import { preloadAddOns } from '../hooks/useAddOns';

const epUrl = (eventId) => `/EventPlayer?eventId=${eventId}`;
const evUrl = (eventId) => `/Event/GetById?eventId=${eventId}`;
const presenceUrl = (eventPlayerId) => `/EventPlayer/${eventPlayerId}/Presence`;
const FUTURE_EVENTS_URL = '/Event/GetFutureCombined';
export const eventMetaKey = (eventId) => `event:${eventId}`;

// Wartezeit aufs Netz, wenn ein lokaler Fallback bereitliegt (Kopie bzw. bereits gesicherter Check-in).
const CACHE_TIMEOUT_MS = 5000;
const PROBE_TIMEOUT_MS = 4000;
const PREFETCH_MAX_AGE_MS = 15 * 60 * 1000;
// Startseite hält zusätzlich zum nächsten Spieltag jedes Event vor, das der Nutzer in den nächsten
// LEADER_PREFETCH_DAYS Tagen leitet (passt zur Spieltagleiter-Erinnerung 3 Tage vorher).
const LEADER_PREFETCH_DAYS = 3;

// axios: Fehler OHNE .response = Netzwerkfehler/Timeout/Offline-Modus. MIT .response = echte HTTP-Antwort.
function isNetworkError(err) {
  return !!err && !err.response;
}

function currentPlayerId() {
  const raw = Number(localStorage.getItem('playerId'));
  return Number.isFinite(raw) && raw > 0 ? raw : null;
}

// Spieltagleiter = EventPlayer mit isOrganizing===true, dessen playerId der eingeloggte Nutzer ist.
export function isLeaderOf(participants) {
  const pid = currentPlayerId();
  if (!pid || !Array.isArray(participants)) return false;
  return participants.some(p => p && p.isOrganizing && p.playerId === pid);
}

async function readCache(url) {
  if (!db.isSupported()) return null;
  try { return (await db.getRead(url)) || null; } catch (e) { return null; }
}

// --- Verbindung ---

// Expliziter Rückweg aus dem Offline-Modus ("Erneut verbinden"). Eine kurze Probe: Kommt IRGENDEINE
// HTTP-Antwort (auch 401 = Sitzung abgelaufen), ist das Netz da -> Online-Modus (Seiten laden neu,
// die Sync-Engine überträgt die Queue). Sonst bleibt die App offline. Liefert true bei Erfolg.
export async function reconnect() {
  if (!isOffline()) return true;
  try {
    await api.get('/Login/CheckAuthentication', { offlineProbe: true, timeout: PROBE_TIMEOUT_MS });
  } catch (err) {
    if (isNetworkError(err)) return false;
  }
  leaveOffline();
  return true;
}

// --- Spieltag ---

async function buildCached(eventId, epRec) {
  const [evRec, meta] = await Promise.all([
    db.getRead(evUrl(eventId)).catch(() => null),
    db.getMeta(eventMetaKey(eventId)).catch(() => null),
  ]);
  return {
    participants: epRec.data ?? [],
    details: evRec ? evRec.data : null,
    fetchedAt: (meta && meta.fetchedAt) || epRec.fetchedAt || null,
    fromCache: true,
    isLeader: true,
  };
}

async function writeEventCache(eventId, eventDate, participants, details, fetchedAt) {
  try {
    await db.putRead(epUrl(eventId), participants);
    await db.putRead(evUrl(eventId), details);
    await db.putMeta(eventMetaKey(eventId), { eventId: Number(eventId), eventDate: eventDate ?? null, fetchedAt });
  } catch (e) { /* best effort */ }
}

async function deleteEventCache(eventId) {
  try {
    await db.deleteRead(epUrl(eventId));
    await db.deleteRead(evUrl(eventId));
    await db.deleteMeta(eventMetaKey(eventId));
  } catch (e) { /* best effort */ }
}

// Lädt Teilnehmerliste + Event-Details. Online: frisch holen; NUR für Leiter lokal cachen (wer nicht
// mehr Leiter ist, dessen alte Kopie wird entfernt). Offline-Modus bzw. Netzwerkfehler: lokale Kopie;
// ohne Kopie { noCache: true }. Echte HTTP-Fehler (403/500…) werden geworfen.
// background: Vorladen ohne wartenden Nutzer -> Standard-Timeout statt des kurzen Cache-Timeouts.
export async function loadEvent(eventId, eventDate, { background = false } = {}) {
  const epRec = await readCache(epUrl(eventId));
  if (epRec && isOffline()) return buildCached(eventId, epRec);

  const cfg = background ? { background: true } : (epRec ? { timeout: CACHE_TIMEOUT_MS } : undefined);
  let epRes, evRes;
  try {
    [epRes, evRes] = await Promise.all([api.get(epUrl(eventId), cfg), api.get(evUrl(eventId), cfg)]);
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    if (epRec) return buildCached(eventId, epRec);
    return { participants: null, details: null, fetchedAt: null, fromCache: true, isLeader: false, noCache: true };
  }

  const participants = epRes.data ?? [];
  const details = evRes.data ?? null;
  const leader = isLeaderOf(participants);
  const fetchedAt = new Date().toISOString();
  if (db.isSupported()) {
    if (leader) await writeEventCache(eventId, eventDate, participants, details, fetchedAt);
    else if (epRec) await deleteEventCache(eventId);
  }
  return { participants, details, fetchedAt, fromCache: false, isLeader: leader };
}

// Nächsten Spieltag im Hintergrund lokal vorhalten (Startseite, nur Orga/Admin). Gedrosselt: je Spieltag
// höchstens ein Versuch pro PREFETCH_MAX_AGE_MS und keiner, solange die lokale Kopie jünger ist.
const lastPrefetchAt = new Map();
export async function prefetchEvent(eventId, eventDate) {
  if (eventId == null || isOffline() || !db.isSupported()) return;
  const now = Date.now();
  if (now - (lastPrefetchAt.get(eventId) || 0) < PREFETCH_MAX_AGE_MS) return;
  lastPrefetchAt.set(eventId, now);
  const meta = await db.getMeta(eventMetaKey(eventId)).catch(() => null);
  const fetchedAt = meta && meta.fetchedAt ? new Date(meta.fetchedAt).getTime() : 0;
  if (now - fetchedAt < PREFETCH_MAX_AGE_MS) return;
  await loadEvent(eventId, eventDate, { background: true });
}

// Event-Zusammenfassungen (GetFutureCombined) -> die Spieltage, die die Startseite lokal vorhält: der nächste
// (wie bisher) und alle weiteren in den nächsten LEADER_PREFETCH_DAYS Tagen — jeweils nur, wenn der Nutzer
// laut leaderPlayerIds Leiter ist. So wird für fremde Events gar keine Teilnehmerliste abgefragt.
function isCancelledEvent(evt) {
  return evt.state === 'Cancelled' || evt.state === 2;
}

function localIsoDate(offsetDays) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function prefetchLeaderEvents(events) {
  if (!Array.isArray(events) || events.length === 0) return;
  const pid = currentPlayerId();
  if (!pid) return;
  const lastDay = localIsoDate(LEADER_PREFETCH_DAYS);
  events.forEach((evt, index) => {
    if (!evt || evt.id == null || isCancelledEvent(evt)) return;
    // Antwort ohne Leiter-Info (älterer API-Stand): wie bisher nur den nächsten Spieltag versuchen —
    // loadEvent prüft die Leitung dann anhand der Teilnehmerliste.
    const known = Array.isArray(evt.leaderPlayerIds);
    if (!known && index !== 0) return;
    if (known && !evt.leaderPlayerIds.includes(pid)) return;
    if (index !== 0 && !(evt.date && evt.date <= lastDay)) return;
    prefetchEvent(evt.id, evt.date).catch(() => {});
  });
}

// --- Check-in ---

async function readCachedParticipant(eventId, eventPlayerId) {
  const rec = await readCache(epUrl(eventId));
  const p = rec && Array.isArray(rec.data) ? rec.data.find(x => x.id === Number(eventPlayerId)) : null;
  return p || null;
}

// Patcht eine Zeile der gecachten Teilnehmerliste (fn: Zeile -> neue Zeile), damit ein Offline-Reload den
// lokalen Stand zeigt (Anwesenheit, Bezahlt-Status). Ohne lokale Kopie (kein Leiter) passiert nichts.
// Exportiert, damit z. B. EventList nach einem manuellen Bezahlt-PUT die Kopie aktuell halten kann.
export async function patchCachedParticipant(eventId, eventPlayerId, fn) {
  if (!db.isSupported()) return;
  try {
    const rec = await db.getRead(epUrl(eventId));
    if (rec && Array.isArray(rec.data)) {
      const id = Number(eventPlayerId);
      const next = rec.data.map(p => (p.id === id ? fn(p) : p));
      await db.putRead(epUrl(eventId), next);
    }
  } catch (e) { /* best effort */ }
}

// Setzt die Anwesenheit — "Outbox-first": Der Tap wird ZUERST lokal gesichert (Queue + Cache-Patch) und
// übersteht so Schließen, Reload und hängende Requests. Online wird er direkt gesendet und danach aus der
// Queue entfernt; im Offline-Modus, bei Netzwerkfehler oder abgelaufener Sitzung (401) bleibt er für die
// Sync-Engine liegen. Andere HTTP-Fehler werden geworfen (lokale Änderung wird zurückgenommen).
// clientTimestamp = Tap-Zeitpunkt -> Backend-LWW.
// autoPaidOnPresence: Schalter des Spieltags — die Auto-bezahlt-Regel (util/payment.js) wird auch auf die
// lokale Kopie angewendet; der Server bestätigt bzw. korrigiert beim Sync.
export async function setPresence(eventPlayerId, eventId, presenceStatus, { autoPaidOnPresence = false } = {}) {
  const clientTimestamp = new Date().toISOString();
  const body = { presenceStatus, clientTimestamp };

  if (!db.isSupported()) {
    const res = await api.put(presenceUrl(eventPlayerId), body, { timeout: CACHE_TIMEOUT_MS });
    return { data: res.data, queued: false };
  }

  const previous = await readCachedParticipant(eventId, eventPlayerId);
  const prevSnapshot = previous ? pickPresencePayment(previous) : null;
  const queueId = await db.upsertQueuePresence({
    eventPlayerId: Number(eventPlayerId),
    eventId: Number(eventId),
    presenceStatus,
    clientTimestamp,
    state: 'pending',
    attempts: 0,
    lastError: null,
  });
  await patchCachedParticipant(eventId, eventPlayerId,
    p => applyPresenceWithAutoPaid(p, presenceStatus, autoPaidOnPresence, clientTimestamp));

  if (isOffline()) return { data: { presenceStatus }, queued: true };

  try {
    const res = await api.put(presenceUrl(eventPlayerId), body, { timeout: CACHE_TIMEOUT_MS });
    // Server-Stand (inkl. Bezahlt-Felder, auch bei per LWW ignoriertem Tap) nur übernehmen, wenn kein
    // neuerer Tap desselben Spielers die Queue-Zeile inzwischen ersetzt hat — dessen lokaler Stand gilt.
    const stillOurs = await db.getQueueItem(queueId).catch(() => null);
    await db.deleteQueueItem(queueId);
    if (stillOurs && res.data) {
      await patchCachedParticipant(eventId, eventPlayerId, p => ({ ...p, ...pickPresencePayment(res.data) }));
    }
    return { data: res.data, queued: false };
  } catch (err) {
    if (isNetworkError(err) || err.response.status === 401) {
      return { data: { presenceStatus }, queued: true };
    }
    // Endgültig abgelehnt: nicht erneut senden. Cache nur zurücknehmen, wenn kein neuerer Tap desselben
    // Spielers die Zeile inzwischen ersetzt hat.
    const stillOurs = await db.getQueueItem(queueId).catch(() => null);
    await db.deleteQueueItem(queueId).catch(() => {});
    if (stillOurs && prevSnapshot) {
      await patchCachedParticipant(eventId, eventPlayerId, p => ({ ...p, ...prevSnapshot }));
    }
    throw err;
  }
}

// EventPlayer-IDs mit ausstehendem/fehlgeschlagenem Sync (für die per-Zeile-Badges).
export async function getPendingPresence(eventId) {
  const empty = { pending: new Set(), failed: new Set() };
  if (!db.isSupported()) return empty;
  try {
    const rows = await db.getQueueByEvent(eventId);
    const pending = new Set();
    const failed = new Set();
    for (const r of (rows || [])) {
      if (r.state === 'failed') failed.add(r.eventPlayerId);
      else pending.add(r.eventPlayerId);
    }
    return { pending, failed };
  } catch (e) {
    return empty;
  }
}

// --- Referenzdaten ---

// Lesende Referenzdaten ohne Personenbezug — für ALLE Nutzer offline verfügbar (Wikis + Preisliste/Zusätze).
// Die Seiten und das Vorladen auf der Startseite müssen dieselben URLs nutzen (= Cache-Schlüssel).
export const REFERENCE_URLS = {
  // All wikis incl. entries in one response (home tiles + every wiki page).
  wikis: '/WikiPage/GetAll',
  // Add-on catalog ("Zusätze") of the price list, article popup and pickers (hooks/useAddOns).
  addOns: '/AddOn/GetAll',
  // Groups incl. archived (snapshot groups of participants) — colors/names of dots offline.
  participantGroups: '/ParticipantGroup/GetAll?includeArchived=true',
  // Role names/colors (Orga/Admin) of the dots and labels offline.
  roleSettings: '/Role/GetSettings',
};

// Shape version of the local copies. On a mismatch (new app version with changed response shapes) the
// event copies of the old shape are dropped and the reference data is refetched right away (online only;
// offline nothing is deleted). The leader events are cached again by the home page prefetch.
const CACHE_EPOCH = 'addons-1';
const CACHE_EPOCH_KEY = 'cacheEpoch';

// Copies of endpoints that no longer exist (the three fixed wiki pages before dynamic wikis, the price list
// before the add-on catalog) — removed from the device on the next prefetch.
const LEGACY_REFERENCE_URLS = ['/Wiki/Get?type=1', '/Wiki/Get?type=2', '/Wiki/Get?type=3', '/Prices/GetAll'];

// Drops every cached event copy (participants, details, meta) and the cached future-events list. The
// check-in queue stays untouched — queued check-ins are still sent.
async function dropEventCopies() {
  try {
    const metas = (await db.getAllMeta()) || [];
    for (const m of metas) {
      if (m.key && m.key.indexOf('event:') === 0) await deleteEventCache(m.eventId);
    }
    await db.deleteRead(FUTURE_EVENTS_URL);
  } catch (e) { /* best effort */ }
}

// Startseite: alle Referenzdaten im Hintergrund lokal ablegen, damit sie offline da sind, ohne dass jede
// Seite einmal geöffnet werden muss. Gedrosselt wie prefetchEvent: höchstens ein Durchlauf pro
// PREFETCH_MAX_AGE_MS, und je URL nur, wenn die lokale Kopie älter ist. background: ein Netzfehler
// schaltet NICHT in den Offline-Modus.
let lastReferencePrefetchAt = 0;
export async function prefetchReferenceData() {
  if (isOffline() || !db.isSupported()) return;
  const now = Date.now();
  if (now - lastReferencePrefetchAt < PREFETCH_MAX_AGE_MS) return;
  lastReferencePrefetchAt = now;
  const epoch = await db.getMeta(CACHE_EPOCH_KEY).catch(() => null);
  const epochChanged = (epoch && epoch.value) !== CACHE_EPOCH;
  // Before anything is cached again: no event copy of the old shape may be rendered offline.
  if (epochChanged) await dropEventCopies();
  LEGACY_REFERENCE_URLS.forEach(url => { db.deleteRead(url).catch(() => {}); });
  // Groups, role settings, wikis and add-ons go through the shared loaders of useParticipantGroups/
  // useRoleSettings/useWikis/useAddOns: a mounted consumer (NextEvent, the wiki tiles on the home page) requests
  // the same URL at the same time, and the loader reuses that request.
  const sharedLoaderUrls = [
    REFERENCE_URLS.participantGroups, REFERENCE_URLS.roleSettings, REFERENCE_URLS.wikis, REFERENCE_URLS.addOns,
  ];
  const urls = Object.values(REFERENCE_URLS).filter(url => !sharedLoaderUrls.includes(url));
  await Promise.all([preloadParticipantGroups(), preloadRoleSettings(), preloadWikis(), preloadAddOns(), ...urls.map(async (url) => {
    const rec = await readCache(url);
    const fetchedAt = rec && rec.fetchedAt ? new Date(rec.fetchedAt).getTime() : 0;
    if (!epochChanged && now - fetchedAt < PREFETCH_MAX_AGE_MS) return;
    try {
      const res = await api.get(url, { background: true });
      if (res.data != null) await db.putRead(url, res.data);
    } catch (e) { /* best effort */ }
  })]);
  if (epochChanged) await db.putMeta(CACHE_EPOCH_KEY, { value: CACHE_EPOCH }).catch(() => {});
}

// Generischer Read-Cache für Referenzdaten ohne eigenes Offline-UI (Zusätze, Wikis, Gruppen, Rollen).
// store(data) entscheidet, ob eine Antwort als lokale Kopie übernommen wird.
export async function cachedGet(url, { store = () => true } = {}) {
  const rec = await readCache(url);
  if (rec && isOffline()) return rec.data;
  try {
    const res = await api.get(url, rec ? { timeout: CACHE_TIMEOUT_MS } : undefined);
    const data = res.data ?? null;
    if (db.isSupported() && store(data)) db.putRead(url, data).catch(() => {});
    return data;
  } catch (err) {
    if (rec && isNetworkError(err)) return rec.data;
    throw err;
  }
}

// Replaces the local copy of a reference URL after a local change (e.g. useWikis after an admin save), so an
// offline reload shows the current state. Best effort; without IndexedDB nothing happens.
export function putCachedRead(url, data) {
  if (!db.isSupported()) return Promise.resolve();
  return db.putRead(url, data).catch(() => {});
}

// Zukünftige Events (Startseite „Nächster Spieltag"). Nur Event-Zusammenfassungen — keine
// personenbezogenen Daten. Wirft nie; ohne Daten -> [].
export async function getFutureEvents() {
  try {
    const data = await cachedGet(FUTURE_EVENTS_URL, { store: d => Array.isArray(d) && d.length > 0 });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    return [];
  }
}
